import { ApplicationConfig, importProvidersFrom, ErrorHandler } from '@angular/core';
import { provideRouter, Routes, withHashLocation } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NAV_ITEMS, NavItem } from './shared/nav-items';
import { AboutService } from './services/about.service';
import { AboutComponent } from './pages/about/about.component';
import { MyKnowledgeDetailsComponent } from './pages/my-knowledge-details/my-knowledge-details.component';
import { AppointmentReminderComponent } from './pages/appointment-reminder/appointment-reminder.component';
import { AppointmentService } from './services/appointment.service';
import { MedicineReminderComponent } from './pages/medicine-reminder/medicine-reminder.component';
import { UserRegistrationComponent } from './pages/user-registration/user-registration.component';
import { UserSurveyComponent } from './pages/user-survey/user-survey.component';
import { TrueStoriesComponent } from './pages/true-stories/true-stories.component';
import { PeriodTrackerComponent } from './pages/period-tracker/period-tracker.component';
import { MyHealthComponent } from './pages/my-health/my-health.component';
import { RecentTipsComponent } from './pages/recent-tips/recent-tips.component';
import { PrivacyPolicyComponent } from './pages/privacy-policy/privacy-policy.component';
import { TermsOfUseComponent } from './pages/terms-of-use/terms-of-use.component';
import { ServiceProviderListComponent } from './pages/service-provider-list/service-provider-list.component';
import { ServiceProviderDetailsComponent } from './pages/service-provider-details/service-provider-details.component';
import { CampaignLandingComponent } from './pages/campaign-landing/campaign-landing.component';
import { CampaignDetailsComponent } from './pages/campaign-details/campaign-details.component';
import { IamCampaignLandingComponent } from './pages/iam-campaign-landing/iam-campaign-landing.component';

import { SubmitATipComponent } from './pages/submit-a-tip/submit-a-tip.component';
import { UserSurveyService } from './services/user-survey.service';
import { BasicAuthInterceptor } from './services/basic-auth.interceptor';
import { ErrorInterceptor } from './services/error.interceptor';
import { GlobalErrorHandler } from './services/global-error-handler';
import { LoggingInterceptor } from './services/logging.interceptor';
import { LoaderInterceptor } from './services/loader.interceptor';
import { LoaderService } from './services/loader.service';
import { ApiConfigService } from './services/api-config.service';
import { WithCredentialsInterceptor } from './services/with-credentials.interceptor';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { environment } from '../environments/environment';

// Function to flatten NAV_ITEMS into a single array of Routes
function createRoutes(items: NavItem[]): Routes {
  let routes: Routes = [];
  items.forEach(item => {
    if (item.route && item.component) {
      const path = item.route.startsWith('/') ? item.route.substring(1) : item.route;
      routes.push({
        path: path,
        component: item.component,
        title: item.label
      });
    }
    if (item.children) {
      routes = routes.concat(createRoutes(item.children));
    }
  });
  return routes;
}

const appRoutes: Routes = [
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { path: 'about', component: AboutComponent },
  { path: 'appointment-reminder', component: AppointmentReminderComponent },
  { path: 'medicine-reminder', component: MedicineReminderComponent },
  
  { path: 'user-registration', component: UserRegistrationComponent },
  { path: 'my-knowledge-details/:id', component: MyKnowledgeDetailsComponent },
  { path: 'user-survey', component: UserSurveyComponent },
  { path: 'true-stories', component: TrueStoriesComponent },
  { path: 'period-tracker', component: PeriodTrackerComponent },
  { path: 'my-health', component: MyHealthComponent },
  { path: 'recent-tips', component: RecentTipsComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'terms-of-use', component: TermsOfUseComponent },
  { path: 'service-provider-list/:service', component: ServiceProviderListComponent },
  { path: 'service-provider-details/:id', component: ServiceProviderDetailsComponent },
  
  { path: 'submit-a-tip', component: SubmitATipComponent },
  { path: 'your-tips/recent-tips', component: RecentTipsComponent },
  { path: 'your-tips/submit-a-tip', component: SubmitATipComponent },
  { path: 'campaign/details/:id', component: CampaignDetailsComponent },
  { path: 'campaign/i-am', component: IamCampaignLandingComponent },
  { path: 'campaign/:slug', component: CampaignLandingComponent },
  ...createRoutes(NAV_ITEMS),
  { path: '**', redirectTo: 'home' } // Wildcard route for a 404-like behavior
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withHashLocation()),
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({
        cookieName: 'csrftoken',
        headerName: 'X-CSRFToken',
      })
    ),
    provideAnimations(),
    provideToastr(),
    importProvidersFrom(HttpClientModule),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: BasicAuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WithCredentialsInterceptor, multi: true },
    AboutService,
    AppointmentService,
    UserSurveyService,
    LoaderService,
    ApiConfigService
  ]
};